import { gql } from '@apollo/client'

export const conversationInboxFragment = gql`
  fragment conversationInboxFragment on Chat {
    _id
    code
    status
    cancelReason
    externalId
    subject
    messageCount
    unreadForAgentCount
    lastMessageAt
    createdAt
    tags
    remarks
    isRequesterLinked
    ccEmails
    assignee {
      _id
      profile {
        _id
        name
        picture
      }
    }
    requester {
      _id
      profile {
        _id
        name
        picture
      }
    }
    sender {
      _id
      company
      profile {
        _id
        name
        email
      }
    }
    metadata {
      _id
      orderId
      purchaseOrderId
      logisticId
    }
    document {
      _id
      code
    }
  }
`

export const conversationInboxQuery = gql`
  ${conversationInboxFragment}
  query allChat($filter: JSON!, $options: JSON) {
    allChat(filter: $filter, options: $options) {
      ...conversationInboxFragment
    }
    allChatMeta(filter: $filter) {
      count
    }
  }
`

// The columns the conversation select draws — shared by the merge and attach dialogs' lists.
export const conversationSelectFragment = gql`
  fragment conversationSelectFragment on Chat {
    _id
    code
    status
    subject
    externalId
    lastMessageAt
    sender {
      _id
      company
      profile {
        _id
        name
        email
      }
    }
    metadata {
      _id
      orderId
      purchaseOrderId
      logisticId
    }
    document {
      _id
      code
    }
  }
`

// The merge dialog's targets: the server returns only the conversations the source can be merged into, with
// the columns the conversation select draws. `filter.sourceChatId` names the source.
export const mergeableChatsQuery = gql`
  ${conversationSelectFragment}
  query getMergeableChats($filter: JSON, $options: JSON) {
    getMergeableChats(filter: $filter, options: $options) {
      ...conversationSelectFragment
    }
  }
`

// The attach dialog's list: the record's customer's conversations, with the columns the conversation select
// draws. `filter.documentKind` and `filter.documentId` name the record.
export const attachableChatsQuery = gql`
  ${conversationSelectFragment}
  query getAttachableChats($filter: JSON, $options: JSON) {
    getAttachableChats(filter: $filter, options: $options) {
      ...conversationSelectFragment
    }
  }
`

// The details panel's own read — one document for the whole rail, selecting the continued-from
// reference the inbox query does not carry. Every mutation below re-selects this same fragment so
// Apollo's normalized cache updates the open panel in place; none of these need an explicit
// `refetchQueries`.
export const conversationDetailsFragment = gql`
  fragment conversationDetailsFragment on Chat {
    _id
    code
    status
    cancelReason
    externalId
    subject
    isRequesterLinked
    ccEmails
    tags
    remarks
    unreadForAgentCount
    lastMessageAt
    createdAt
    assignee {
      _id
      profile {
        _id
        name
        picture
      }
    }
    requester {
      _id
      status
      type
      phone
      profile {
        _id
        name
        email
        picture
      }
      company {
        _id
        name
      }
    }
    sender {
      _id
      company
      phone
      profile {
        _id
        name
        email
      }
    }
    metadata {
      _id
      orderId
      purchaseOrderId
      logisticId
    }
    document {
      _id
      code
    }
    continuedFrom {
      _id
      code
      externalId
    }
  }
`

// `mergedIntoCode` costs the server one read per row that selects it, so it rides this single-document query
// and not the fragment the list-shaped reads and the mutations share.
export const conversationDetailsQuery = gql`
  ${conversationDetailsFragment}
  query allChat($filter: JSON!, $options: JSON) {
    allChat(filter: $filter, options: $options) {
      ...conversationDetailsFragment
      mergedInto
      mergedIntoCode
    }
  }
`

export const assignChatMutation = gql`
  ${conversationDetailsFragment}
  mutation assignChat($input: AssignChatArgs!) {
    assignChat(input: $input) {
      ...conversationDetailsFragment
    }
  }
`

export const linkChatRequesterMutation = gql`
  ${conversationDetailsFragment}
  mutation linkChatRequester($input: LinkChatRequesterArgs!) {
    linkChatRequester(input: $input) {
      ...conversationDetailsFragment
    }
  }
`

// Selects only what the attach reads back: the three document keys and the snapshot. The document's own
// `activeConversationId` is refetched by the host page, so nothing here touches the document.
export const linkChatToDocumentMutation = gql`
  mutation linkChatToDocument($input: LinkChatToDocumentArgs!) {
    linkChatToDocument(input: $input) {
      _id
      metadata {
        _id
        orderId
        purchaseOrderId
        logisticId
      }
      document {
        _id
        code
      }
    }
  }
`

// Selects what the unlink clears on the cached chat: the three document keys and the snapshot. The document's
// own `activeConversationId` is refetched by the host page, like the attach.
export const unlinkChatFromDocumentMutation = gql`
  mutation unlinkChatFromDocument($input: UnlinkChatFromDocumentArgs!) {
    unlinkChatFromDocument(input: $input) {
      _id
      metadata {
        _id
        orderId
        purchaseOrderId
        logisticId
      }
      document {
        _id
        code
      }
    }
  }
`

export const addChatCcMutation = gql`
  ${conversationDetailsFragment}
  mutation addChatCc($input: AddChatCcArgs!) {
    addChatCc(input: $input) {
      ...conversationDetailsFragment
    }
  }
`

export const removeChatCcMutation = gql`
  ${conversationDetailsFragment}
  mutation removeChatCc($input: RemoveChatCcArgs!) {
    removeChatCc(input: $input) {
      ...conversationDetailsFragment
    }
  }
`

export const updateChatTagsMutation = gql`
  ${conversationDetailsFragment}
  mutation updateChatTags($input: UpdateChatTagsArgs!) {
    updateChatTags(input: $input) {
      ...conversationDetailsFragment
    }
  }
`

export const updateChatRemarksMutation = gql`
  ${conversationDetailsFragment}
  mutation updateChatRemarks($input: UpdateChatRemarksArgs!) {
    updateChatRemarks(input: $input) {
      ...conversationDetailsFragment
    }
  }
`

export const addChatInternalNoteMutation = gql`
  mutation addChatInternalNote($input: AddChatInternalNoteArgs!) {
    addChatInternalNote(input: $input) {
      _id
    }
  }
`

export const updateChatSubjectMutation = gql`
  ${conversationDetailsFragment}
  mutation updateChatSubject($input: UpdateChatSubjectArgs!, $filter: JSON) {
    updateChatSubject(input: $input, filter: $filter) {
      ...conversationDetailsFragment
    }
  }
`

export const changeChatStatusMutation = gql`
  ${conversationDetailsFragment}
  mutation changeChatStatus($input: StateTransitionArgs!) {
    changeChatStatus(input: $input) {
      ...conversationDetailsFragment
    }
  }
`

// One result shape serves all six batch operations — `ChatBatchResult` carries no `_id`,
// so nothing here selects one; `chatId` on each failure already identifies the conversation.
export const batchCloseChatsMutation = gql`
  mutation batchCloseChats($input: BatchCloseChatsArgs!) {
    batchCloseChats(input: $input) {
      succeededCount
      failedCount
      failures {
        chatId
        reason
      }
    }
  }
`

export const batchCancelChatsMutation = gql`
  mutation batchCancelChats($input: BatchCancelChatsArgs!) {
    batchCancelChats(input: $input) {
      succeededCount
      failedCount
      failures {
        chatId
        reason
      }
    }
  }
`

export const batchAssignChatsMutation = gql`
  mutation batchAssignChats($input: BatchAssignChatsArgs!) {
    batchAssignChats(input: $input) {
      succeededCount
      failedCount
      failures {
        chatId
        reason
      }
    }
  }
`

export const batchUpdateChatTagsMutation = gql`
  mutation batchUpdateChatTags($input: BatchUpdateChatTagsArgs!) {
    batchUpdateChatTags(input: $input) {
      succeededCount
      failedCount
      failures {
        chatId
        reason
      }
    }
  }
`

export const batchUpdateChatRemarksMutation = gql`
  mutation batchUpdateChatRemarks($input: BatchUpdateChatRemarksArgs!) {
    batchUpdateChatRemarks(input: $input) {
      succeededCount
      failedCount
      failures {
        chatId
        reason
      }
    }
  }
`

export const batchReplyToChatsMutation = gql`
  mutation batchReplyToChats($input: BatchReplyToChatsArgs!) {
    batchReplyToChats(input: $input) {
      succeededCount
      failedCount
      failures {
        chatId
        reason
      }
    }
  }
`

export const markChatAsSpamMutation = gql`
  mutation markChatAsSpam($input: MarkChatAsSpamArgs!) {
    markChatAsSpam(input: $input) {
      _id
      status
      cancelReason
    }
  }
`

export const unmarkChatAsSpamMutation = gql`
  mutation unmarkChatAsSpam($input: UnmarkChatAsSpamArgs!) {
    unmarkChatAsSpam(input: $input) {
      _id
      status
      cancelReason
    }
  }
`

export const batchMarkChatsAsSpamMutation = gql`
  mutation batchMarkChatsAsSpam($input: BatchMarkChatsAsSpamArgs!) {
    batchMarkChatsAsSpam(input: $input) {
      succeededCount
      failedCount
      failures {
        chatId
        reason
      }
    }
  }
`

export const batchMarkChatsReadMutation = gql`
  mutation batchMarkChatsRead($input: BatchMarkChatsReadArgs!) {
    batchMarkChatsRead(input: $input) {
      succeededCount
      failedCount
      failures {
        chatId
        reason
      }
    }
  }
`
